/**
 * useIdleWatcher — client-side companion to the server's 30-minute sliding
 * session idle timeout. See /app/backend/server.py `get_current_user()` for
 * the authoritative enforcement — this hook is purely UX so users know
 * they're about to be signed out and can extend the session with one click.
 *
 * How it works:
 *   1. Fetches SESSION_IDLE_TIMEOUT_MINUTES and SESSION_WARNING_SECONDS from
 *      GET /api/auth/session-config on mount so policy stays server-driven.
 *   2. Tracks the wall-clock timestamp of the last user activity
 *      (mousemove / keydown / click / scroll / touchstart). All handlers
 *      are passive + throttled to 5 seconds to keep this cheap on chatty
 *      pages like the dashboard.
 *   3. Every second computes remaining = idleTimeout - (now - lastActivity)
 *      and exposes state:
 *        - showWarning: true when 0 < remaining ≤ warningSeconds
 *        - forceLogout: true when remaining ≤ 0
 *   4. Sends POST /api/auth/heartbeat whenever the user is genuinely active
 *      and it's been >30s since the last heartbeat. This resets the
 *      server-side sliding window without spamming Mongo.
 *
 * The caller (IdleWarningModal + Layout) renders a modal on showWarning and
 * calls stay()/logout() on the returned API.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { authAPI } from '../lib/api';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'];
const ACTIVITY_THROTTLE_MS = 5000;         // don't touch lastActivity more than 1x / 5s
const HEARTBEAT_MIN_INTERVAL_MS = 30000;   // don't POST /heartbeat more than 1x / 30s
const TICK_MS = 1000;                      // countdown resolution

export function useIdleWatcher({ enabled = true } = {}) {
  const [config, setConfig] = useState(null);       // {idle_timeout_minutes, warning_seconds}
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(null);

  const lastActivityRef = useRef(Date.now());
  const lastHeartbeatRef = useRef(0);
  const forcedLogoutRef = useRef(false);

  // ── Fetch policy once ────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    authAPI.sessionConfig()
      .then((r) => { if (!cancelled) setConfig(r.data); })
      .catch(() => { if (!cancelled) setConfig({ idle_timeout_minutes: 30, warning_seconds: 60 }); });
    return () => { cancelled = true; };
  }, [enabled]);

  // ── Wire activity listeners ──────────────────────────────────────
  useEffect(() => {
    if (!enabled || !config) return;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) return;
      lastActivityRef.current = now;
      // If the warning modal is up and the user goes back to interacting,
      // treat that as an implicit "stay signed in".
      setShowWarning(false);
      // Only heartbeat if throttle window elapsed AND the server timer needs
      // a nudge (>30s since our last ping).
      if (now - lastHeartbeatRef.current >= HEARTBEAT_MIN_INTERVAL_MS) {
        lastHeartbeatRef.current = now;
        authAPI.heartbeat().catch(() => { /* interceptor handles auth failures */ });
      }
    };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
  }, [enabled, config]);

  // ── Countdown tick ───────────────────────────────────────────────
  useEffect(() => {
    if (!enabled || !config) return;
    const timeoutMs = config.idle_timeout_minutes * 60 * 1000;
    const warningMs = config.warning_seconds * 1000;
    const id = setInterval(() => {
      if (forcedLogoutRef.current) return;
      const idleMs = Date.now() - lastActivityRef.current;
      const remainingMs = timeoutMs - idleMs;
      if (remainingMs <= 0) {
        // Idle timeout hit — end the session server-side then bounce to
        // /login. This is defensive: the very next authenticated API call
        // would also fail with `session_idle_expired`, but firing an
        // explicit logout keeps the sessions collection clean.
        forcedLogoutRef.current = true;
        setShowWarning(false);
        authAPI.logout()
          .catch(() => { /* nothing meaningful to do */ })
          .finally(() => {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login?reason=idle';
          });
        return;
      }
      setSecondsRemaining(Math.ceil(remainingMs / 1000));
      setShowWarning(remainingMs <= warningMs);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [enabled, config]);

  // Called by the modal's "Stay signed in" button — same thing an activity
  // event would do, but explicit and immediate.
  const stay = useCallback(async () => {
    lastActivityRef.current = Date.now();
    lastHeartbeatRef.current = Date.now();
    setShowWarning(false);
    try { await authAPI.heartbeat(); } catch (_e) { /* interceptor handles it */ }
  }, []);

  const signOutNow = useCallback(async () => {
    forcedLogoutRef.current = true;
    try { await authAPI.logout(); } catch (_e) { /* nothing to do */ }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login?reason=manual';
  }, []);

  return { showWarning, secondsRemaining, config, stay, signOutNow };
}
